import { useMemo } from "react";
import { AxiosError } from "axios";
import { isObject } from "lodash";
import { isBlank } from "@/utils/strings";
import { ApiService, HttpStatus, isHttpError } from "@/api";
import { logDebug } from "@/utils/log";
import { fromQueryString, singleValue } from "@/utils/queryParams";
import UserStore, { LoginStatus } from "@/stores/UserStore";
import { History } from "history";
import { makeAutoObservable } from "mobx";
import { TMessage } from "@/i18n/tt";
import { useRootStore } from "@/stores/RootStoreContext";
import { minLengthValidator, StringFieldModel } from "@/common/commonForms";
import { validateName } from "@/utils/Roles";
import { emailValidator } from "@/common/common";
import { AlertState } from "@/common/alertConfirm";
import { CreateUserInvitation } from "@/types/Invitation";

export const loginRedirectKey = "dcompany-login-redirect";
export const ssoConnectedKey = "dcompany-sso-connected";
export type SsoConnectedProvider = "google" | "microsoft";

export function isSsoConnectedProvider(value: string | null): value is SsoConnectedProvider {
  return value === "google" || value === "microsoft";
}

export function createSsoConnectedAlert(provider: SsoConnectedProvider): AlertState {
  return {
    text: {
      key: "ssoConnected.text",
      params: { provider: provider === "google" ? "Google" : "Microsoft" },
    },
    image: {
      src: provider === "google" ? "/Google-success.gif" : "/Microsoft-success.gif",
      style: { width: "25%", height: "auto" },
    },
  };
}

export function createSsoDomainNotActivatedAlert(domain: string): AlertState {
  return {
    title: { translated: "" },
    text: {
      key: "sso.domain-not-activated.description",
      params: { domain },
    },
  };
}

export type LoginMethod =
  | "username"
  | "norwegianBankId"
  | "swedishBankId"
  | "danishMitId"
  | "finnishFtn"
  | "microsoft"
  | "google"
  | "create-user";

type LoginState = "input" | "loading" | "error" | "throttled" | "account-locked";
type MfaState =
  | "input"
  | "submitting"
  | "invalid-code"
  | "unknown-error"
  | "sending-new-code"
  | "resend-throttled"
  | "account-locked";

export interface LoginModel {
  disableIntroText?: boolean;
  disableLogo?: boolean;
  disableUsernamePasswordLogin?: boolean;
  loginView?: "username" | "forgot-password" | "create-user";
  loginState: LoginState;
  throttledForSeconds?: number;
  loginStatus: LoginStatus;
  mfaState: MfaState;
  showBankIdWarning?: boolean;
  clearLoginMethod: () => void;
  login: () => void;
  loginWithUsername: (username: string, password: string) => void;
  createUser: (model: CreateUserModel) => void;
  selectLoginMethod: (method: LoginMethod) => void;
  activeLoginMethod?: LoginMethod;
  sendNewOneTimeCode: () => void;
  submitOneTimeCode: () => void;
  handleLoginCallback: () => void;
  initRedirectUrl: () => void;
  initAffiliateKey: () => void;
  checkBankIdStatus: () => void;
  forgotPassword: () => void;
  passwordResetRequestState: PasswordResetRequestState;
  resetPassword: () => void;
  createUserModel?: CreateUserModel;
  username: StringFieldModel;
  password: StringFieldModel;
  mfaToken: StringFieldModel;
  email: StringFieldModel;
}

export class MLoginModel implements LoginModel {
  history: History;
  apiService: ApiService;
  userStore: UserStore;
  disableIntroText?: boolean;
  disableLogo?: boolean;
  disableUsernamePasswordLogin?: boolean;
  loginView?: "username" | "forgot-password" | "create-user";
  loginState: LoginState = "input";
  throttledForSeconds?: number;
  mfaState: MfaState = "input";
  showBankIdWarning?: boolean;
  passwordResetRequestState: PasswordResetRequestState = "input";
  createUserModel?: CreateUserModel;
  username: StringFieldModel;
  password: StringFieldModel;
  mfaToken: StringFieldModel;
  email: StringFieldModel;

  constructor(params: {
    history: History;
    apiService: ApiService;
    userStore: UserStore;
    disableIntroText?: boolean;
    disableLogo?: boolean;
    disableUsernamePasswordLogin?: boolean;
    createUserModel?: CreateUserModel;
  }) {
    this.history = params.history;
    this.apiService = params.apiService;
    this.userStore = params.userStore;
    this.createUserModel = params.createUserModel;
    this.disableIntroText = params.disableIntroText;
    this.disableLogo = params.disableLogo;
    this.disableUsernamePasswordLogin = params.disableUsernamePasswordLogin;
    this.username = new StringFieldModel();
    this.password = new StringFieldModel();
    this.mfaToken = new StringFieldModel();
    this.email = new StringFieldModel({ required: true, validator: emailValidator });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get loginStatus() {
    return this.userStore.loginStatus;
  }

  clearLoginMethod() {
    this.loginView = undefined;
    this.loginState = "input";
    this.throttledForSeconds = undefined;
    this.passwordResetRequestState = "input";
    this.userStore.clearMfaChallenge();
    this.username.clear();
    this.password.clear();
  }

  activeLoginMethod?: LoginMethod;

  selectLoginMethod(method: LoginMethod) {
    this.activeLoginMethod = method;
    switch (method) {
      case "username":
        this.loginView = "username";
        break;
      case "norwegianBankId":
        location.href = `/api/auth/bankid${this.userStore.authenticationUrlParameters}`;
        break;
      case "swedishBankId":
        location.href = `/api/auth/bankid-sweden${this.userStore.authenticationUrlParameters}`;
        break;
      case "danishMitId":
        location.href = `/api/auth/mitid${this.userStore.authenticationUrlParameters}`;
        break;
      case "finnishFtn":
        location.href = `/api/auth/ftn${this.userStore.authenticationUrlParameters}`;
        break;
      case "microsoft":
        location.href = `/api/auth/microsoft${this.userStore.authenticationUrlParameters}`;
        break;
      case "google":
        location.href = `/api/auth/google${this.userStore.authenticationUrlParameters}`;
        break;
      case "create-user":
        this.loginView = "create-user";
        break;
    }
  }

  login() {
    this.username.validate();
    this.password.validate();
    this.loginWithUsername(this.username.validatedValue, this.password.validatedValue);
  }

  *loginWithUsername(username: string, password: string) {
    if (this.loginState === "loading") {
      return;
    }
    if (isBlank(username) || isBlank(password)) {
      this.loginState = "error";
      return;
    }

    this.loginState = "loading";
    try {
      const token: string = yield this.apiService.getCsrfToken();
      yield this.apiService.logon(username, password, token);
      location.replace("/login-callback");
    } catch (err) {
      logDebug("login error", err);
      if (isHttpError(HttpStatus.TOO_MANY_REQUESTS, err)) {
        this.throttledForSeconds = retryAfterSeconds(err);
        this.loginState = "throttled";
      } else if (isHttpError(HttpStatus.LOCKED, err)) {
        this.loginState = "account-locked";
      } else {
        this.loginState = "error";
      }
    }
  }

  *createUser(model: CreateUserModel) {
    if (this.loginState !== "loading" && model.validate()) {
      this.loginState = "loading";
      try {
        const token: string = yield this.apiService.getCsrfToken();
        yield this.apiService.createUser(
          model.name.validatedValue,
          model.password.validatedValue,
          model.invitation,
          token,
        );
        location.replace("/login-callback");
      } catch (err) {
        this.loginState = "error";
      }
    }
  }

  *submitOneTimeCode() {
    if (this.mfaState === "submitting") {
      return;
    }
    const code = this.mfaToken.validatedValue;
    if (isBlank(code)) {
      this.mfaState = "invalid-code";
      return;
    }
    this.mfaState = "submitting";
    try {
      yield this.apiService.postOneTimeCode(code);
      location.replace("/login-callback");
    } catch (err) {
      if (isHttpError(HttpStatus.BAD_REQUEST, err)) {
        this.mfaState = "invalid-code";
      } else if (isHttpError(HttpStatus.LOCKED, err)) {
        this.mfaState = "account-locked";
      } else {
        this.mfaState = "unknown-error";
      }
    }
  }

  *sendNewOneTimeCode() {
    if (this.mfaState === "sending-new-code") {
      return;
    }
    this.mfaToken.clear();
    this.mfaState = "sending-new-code";
    try {
      yield this.apiService.sendNewOneTimeCode();
      this.mfaState = "input";
    } catch (err) {
      if (isHttpError(HttpStatus.UNAUTHORIZED, err)) {
        this.mfaState = "input";
      } else if (isHttpError(HttpStatus.TOO_MANY_REQUESTS, err)) {
        this.throttledForSeconds = retryAfterSeconds(err);
        this.mfaState = "resend-throttled";
      } else {
        this.mfaState = "unknown-error";
      }
    }
  }

  initRedirectUrl() {
    if (!location.pathname.includes("/login")) {
      sessionStorage.setItem(
        loginRedirectKey,
        `${location.pathname}${location.search}${location.hash}`,
      );
    }
  }

  *initAffiliateKey() {
    const affiliateKey = singleValue(fromQueryString(location.search).affiliate);
    if (affiliateKey) {
      try {
        yield this.apiService.addAffiliateToSession(affiliateKey);
      } catch (err) {
        // no action
      }
    }
  }

  *checkBankIdStatus() {
    try {
      const status: BankIdStatus = yield this.apiService.fetchBankIdStatus();
      this.showBankIdWarning = status === "ERROR";
    } catch (err) {
      logDebug("Failed to fetch BankId status", err);
    }
  }

  handleLoginCallback() {
    const params = new URLSearchParams(window.location.search);
    const ssoConnected = params.get("sso_connected");
    if (ssoConnected) {
      sessionStorage.setItem(ssoConnectedKey, ssoConnected);
    }
    const redirectUrl = this.userStore.isLoginRequired
      ? "/login"
      : sessionStorage.getItem(loginRedirectKey);
    sessionStorage.removeItem(loginRedirectKey);
    this.history.replace(redirectUrl ?? "/portfolio");
  }

  forgotPassword() {
    this.loginView = "forgot-password";
  }

  *resetPassword() {
    if (this.passwordResetRequestState === "submitting") {
      return;
    }
    if (this.email.validate()) {
      try {
        this.passwordResetRequestState = "submitting";
        yield this.apiService.passwordResetRequest(this.email.validatedValue);
        this.passwordResetRequestState = "sent";
      } catch (e) {
        this.passwordResetRequestState = "server-error";
      }
    }
  }
}

export const useLoginModel = ({
  disableLogo,
  disableIntroText,
  disableUsernamePasswordLogin,
  canCreateUser,
  username,
  invitation,
}: {
  disableIntroText?: boolean;
  disableLogo?: boolean;
  disableUsernamePasswordLogin?: boolean;
  canCreateUser?: boolean;
  username?: string;
  invitation?: CreateUserInvitation;
} = {}): LoginModel => {
  const history = useRootStore().history;
  const { apiService, userStore } = useRootStore();
  return useMemo(
    () =>
      new MLoginModel({
        history,
        apiService,
        userStore,
        disableIntroText,
        disableLogo,
        disableUsernamePasswordLogin,
        createUserModel:
          canCreateUser && username && invitation
            ? new CreateUserModel(username, invitation)
            : undefined,
      }),
    [
      apiService,
      disableIntroText,
      disableLogo,
      disableUsernamePasswordLogin,
      history,
      userStore,
      canCreateUser,
      username,
      invitation,
    ],
  );
};

export type PasswordResetRequestState = "input" | "submitting" | "server-error" | "sent";

export interface PasswordResetModel {
  requestNewPassword: () => void;
  submitting: boolean;
  state: PasswordResetState;
  passwordModel: StringFieldModel;
  repeatPasswordModel: StringFieldModel;
  error?: TMessage;
  checkToken: () => void;
}

export type PasswordResetState =
  | "reference-check"
  | "input"
  | "submitting"
  | "invalid-token"
  | "success";

export const usePasswordResetModel = ({ token }: { token: string | null }): PasswordResetModel => {
  const { apiService } = useRootStore();
  return useMemo(() => new MPasswordResetModel(token, apiService), [apiService, token]);
};

export class MPasswordResetModel implements PasswordResetModel {
  readonly token: string | null;
  state: PasswordResetState = "reference-check";

  passwordModel: StringFieldModel = new StringFieldModel({
    required: true,
    validator: minLengthValidator(8, { key: "forgotpassword.password-length" }),
  });

  repeatPasswordModel: StringFieldModel = new StringFieldModel({
    required: true,
    validator: repeatPasswordValidator(this.passwordModel),
  });

  error?: TMessage;

  constructor(
    token: string | null,
    readonly apiService: ApiService,
  ) {
    this.token = token;
    makeAutoObservable(this, { apiService: false }, { autoBind: true });
  }

  setState(state: PasswordResetState) {
    this.state = state;
  }

  *checkToken() {
    if (this.token === null) {
      this.error = { key: "forgotpassword.invalid-token" };
    } else {
      try {
        yield this.apiService.checkPasswordResetToken(this.token);
        this.setState("input");
      } catch (e) {
        this.setState("invalid-token");
      }
    }
  }

  validate() {
    this.passwordModel.validate();
    this.repeatPasswordModel.validate();
  }

  get isValid(): boolean {
    return this.passwordModel.isValid && this.repeatPasswordModel.isValid;
  }

  *requestNewPassword() {
    this.validate();
    if (this.isValid) {
      this.setState("submitting");
      try {
        yield this.apiService.passwordResetSetNewPassword(
          this.token!,
          this.passwordModel.validatedValue,
        );
        this.setState("success");
      } catch (e) {
        this.error = { key: "forgotpassword.reset-failed" };
        this.setState("input");
      }
    }
  }

  get submitting(): boolean {
    return this.state === "submitting";
  }
}

export class CreateUserModel {
  username: StringFieldModel;
  name: StringFieldModel = new StringFieldModel({
    required: true,
    validator: validateName,
  });

  password: StringFieldModel = new StringFieldModel({
    required: true,
    validator: minLengthValidator(8, { key: "forgotpassword.password-length" }),
  });

  repeatPassword: StringFieldModel = new StringFieldModel({
    required: true,
    validator: repeatPasswordValidator(this.password),
  });

  error?: TMessage;

  constructor(
    username: string,
    readonly invitation: CreateUserInvitation,
  ) {
    this.username = new StringFieldModel({ value: username });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  validate(): boolean {
    this.name.validate();
    this.password.validate();
    this.repeatPassword.validate();
    return this.isValid;
  }

  get isValid(): boolean {
    return this.name.isValid && this.password.isValid && this.repeatPassword.isValid;
  }
}

function retryAfterSeconds(err: AxiosError): number | undefined {
  const data: unknown = err.response?.data;
  return isObject(data) && "retryAfterSeconds" in data && typeof data.retryAfterSeconds === "number"
    ? data.retryAfterSeconds
    : undefined;
}

function repeatPasswordValidator(
  password: StringFieldModel,
): (value: string) => TMessage | undefined {
  return (value) => {
    if (password.validatedValue !== value) {
      return { key: "forgotpassword.forgot-password-not-match" };
    }
  };
}

type BankIdStatus = "OK" | "ERROR";
